// -----------------------------------------------------------------Imports---
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
  Box,
  Button,
  Container,
  Dialog,
  Grid,
  IconButton,
  Modal,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Divider } from "@mui/material";
import React, { Key, useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useDebounce } from "usehooks-ts";

import LoadingDialog from "../../components/LoadingDialog/LoadingDialog";
import { Accordion, AccordionDetails, AccordionSummary } from "../../components/TableAccordions/TableAccordions";
import Transition from "../../components/Transition/Transition";
import { BooleanDictionary } from "../../GlobalTypes";
import {
  DeliveryAddressGridKey,
  DeliveryAddressModel,
  gridKeys as DeliveryGridKeys,
} from "../../models/DeliveryAddressModel";
import { PartnerModel, PartnerTableKey, tableKeys, ToDeleteDelivery } from "../../models/PartnerModel";
import ResponseModel from "../../models/ResponseModel";
import DeliveryAddressService from "../../services/deliveryAddresses/DeliveryAddressService";
import PartnerService from "../../services/partners/PartnerService";
import { doneChecker, getLongAddress, hasApiError } from "../../utils/Utils";
import AddDeliveryAddress from "../deliveryAddresses/AddDeliveryAddress";

import AddPartner from "./AddPartner";
import { style } from "./Partners.style";

type PartnersDone = BooleanDictionary & {
  partners: boolean;
};

// -----------------------------------------------------------------Exports---
const Partners = (): JSX.Element => {
  const [partners, setPartners] = useState<PartnerModel[]>([]);
  const [rowsPerPage, setRowsPerPage] = useState<number>(50);
  const [maxData, setMaxData] = useState<number>(0);
  const [page, setPage] = useState<number>(0);
  const [nameFilter, setNameFilter] = useState<string>("");
  const debouncedName: string = useDebounce<string>(nameFilter, 1000);
  const [VATFilter, setVATFilter] = useState<string>("");
  const debouncedVAT: string = useDebounce<string>(VATFilter, 1000);
  const [expanded, setExpanded] = useState<number | false>(false);
  const [isShowingDeleteDeliveryModal, setShowingDeleteDeliveryModal] = useState<boolean>(false);
  const [toDeleteDelivery, setToDeleteDelivery] = useState<ToDeleteDelivery>({} as ToDeleteDelivery);
  const [isSetterOpen, setSetterOpen] = useState<boolean>(false);
  const [updateID, setUpdateID] = useState<number | undefined>(undefined);
  const [isDeliverySetterOpen, setDeliverySetterOpen] = useState<boolean>(false);
  const [updateDeliveryID, setUpdateDeliveryID] = useState<number | string | undefined>(undefined);
  const [partnerForDelivery, setPartnerForDelivery] = useState<PartnerModel | null>(null);
  const [done, setDone] = useState<PartnersDone>({ partners: false });

  //render the head once
  const headMemo: JSX.Element[] = useMemo((): JSX.Element[] => {
    return tableKeys.map((tableKey: PartnerTableKey, i: Key): JSX.Element => {
      return (
        <TableCell key={i} style={style.tableHeadCell} align={tableKey.align ?? "left"}>
          {tableKey.label}
        </TableCell>
      );
    });
  }, []);

  //paging
  const handleChangePage = (_event: unknown, newPage: number): void => {
    setPage(newPage);
  };

  //changing row amount
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setRowsPerPage(parseInt(event.target.value));
    setPage(0);
  };

  //accordion handling
  const accordionChange = (panel: number) => (): void => {
    if (expanded === panel) setExpanded(false);
    else setExpanded(panel);
  };

  //get the data
  const getPartners = useCallback(async (): Promise<void> => {
    const filter: { name?: string; taxNumber?: string } = {};
    if (debouncedName.length > 2) filter.name = debouncedName.toLowerCase();
    if (debouncedVAT.length > 2) filter.taxNumber = debouncedVAT.toLowerCase();

    const pagination: { currentPage: number; dataPerPage: number } | undefined =
      debouncedName.length <= 2 && debouncedVAT.length <= 2
        ? { currentPage: page + 1, dataPerPage: rowsPerPage }
        : undefined;

    const partnersResponse: ResponseModel = await PartnerService.getPartners({ filter, pagination });
    //showing the result
    if (hasApiError(partnersResponse)) {
      setDone((prev: PartnersDone): PartnersDone => ({ ...prev, partners: true }));
      return;
    }

    const partnersData: PartnerModel[] = partnersResponse.data as PartnerModel[];

    partnersData.forEach((partner: PartnerModel): void => {
      partner.fullAddress = getLongAddress(partner);
      (partner.deliveryAddresses ?? []).forEach((address: DeliveryAddressModel): void => {
        address.fullAddress = getLongAddress(address);
      });
    });
    const totalCount: number = partnersResponse.pagination?.totalDataCount ?? 0;
    if (page * rowsPerPage > totalCount) setPage(0);
    setMaxData(totalCount);
    setPartners(partnersData);

    if (!done.partners) setDone((prev: PartnersDone): PartnersDone => ({ ...prev, partners: true }));
  }, [debouncedName, debouncedVAT, done.partners, page, rowsPerPage]);

  //filtering the data
  const filterOnChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    _key: string,
    setter: React.Dispatch<React.SetStateAction<string>>,
  ): void => {
    //update the state  with the new value
    setter(event.target.value);
  };

  //show a second window before deleting a deliveryAddress, prep the data
  const onDeliveryDeleteButton = (_toDeleteDelivery: ToDeleteDelivery): void => {
    setToDeleteDelivery(_toDeleteDelivery);
    setShowingDeleteDeliveryModal(true);
  };

  //delete the selected deliveryAddress
  const deleteDelivery = useCallback(async (): Promise<void> => {
    const loadingToastId = toast.loading("Törlés folyamatban...");
    const response: ResponseModel = await DeliveryAddressService.deleteDeliveryAddress(toDeleteDelivery.id);

    if (hasApiError(response)) {
      toast.dismiss(loadingToastId);
      return;
    } else {
      toast.update(loadingToastId, { render: "Sikeres szállítási cím törlés", type: "success", isLoading: false, autoClose: 4000 });
      void getPartners();
      setShowingDeleteDeliveryModal(false);
    }
  }, [getPartners, toDeleteDelivery.id]);

  //show modal for creating new partner
  const onNewPartner = (): void => {
    setUpdateID(undefined);
    setSetterOpen(true);
  };

  //show modal for creating new deliveryAddress
  const onNewDeliveryAddress = (partner: PartnerModel): void => {
    setUpdateDeliveryID(undefined);
    setPartnerForDelivery(partner);
    setDeliverySetterOpen(true);
  };

  //show modal for updating a partner
  const onUpdate = (partnerCode: number): void => {
    setUpdateID(partnerCode);
    setSetterOpen(true);
  };

  //show modal for updating a deliveryAddress
  const onUpdateDelivery = (deliveryAddressCode: number): void => {
    setUpdateDeliveryID(deliveryAddressCode.toString());
    setDeliverySetterOpen(true);
  };

  // get data on load
  useEffect((): void => {
    void getPartners();
  }, [rowsPerPage, page, debouncedName, debouncedVAT]);

  // blocking with a dialog while getting all the data
  if (!doneChecker(done)) {
    return <LoadingDialog />;
  }

  // --------------------------------------------------------------Return---
  return (
    <Paper style={style.paper}>
      {/* Header */}
      <Typography variant="h3" style={style.title}>
        Ügyfelek listája
      </Typography>
      <Container style={style.container}>
        <Box>
          <TextField
            variant="outlined"
            value={nameFilter}
            onChange={(e: React.ChangeEvent<HTMLInputElement>): void => filterOnChange(e, "name", setNameFilter)}
            style={style.filterTextField}
            label="Cégnév"
          />
          <TextField
            variant="outlined"
            value={VATFilter}
            onChange={(e: React.ChangeEvent<HTMLInputElement>): void => filterOnChange(e, "taxNumber", setVATFilter)}
            style={style.filterTextField}
            label="Adószám"
          />
        </Box>
        <Button onClick={onNewPartner} variant="contained" style={style.navButton}>
          Új ügyfél
        </Button>
      </Container>

      {/* Table */}
      <TableContainer style={style.tableContainer}>
        <Table>
          <TableHead>
            <TableRow>{headMemo}</TableRow>
          </TableHead>
          <TableBody>
            {partners.map((partner: PartnerModel, i: Key): JSX.Element => {
              return (
                <React.Fragment key={i}>
                  {/* Partner data */}
                  <TableRow onClick={accordionChange(partner.partnerCode)} style={style.dataRow}>
                    {tableKeys.map((tableKey: PartnerTableKey, j: number): JSX.Element => {
                      return (
                        <TableCell align={tableKey.align ?? "left"} style={style.dataCell} key={j}>
                          {(partner as Record<string, unknown>)[tableKey.key] as string}
                        </TableCell>
                      );
                    })}
                  </TableRow>

                  {/* DeliveryAddresses */}
                  <TableRow>
                    <TableCell style={style.accordionCell} colSpan={tableKeys.length}>
                      <Accordion expanded={expanded === partner.partnerCode}>
                        <AccordionSummary />
                        <AccordionDetails style={style.accordionDetails}>
                          {(partner.deliveryAddresses ?? []).map(
                            (address: DeliveryAddressModel, j: Key): JSX.Element => {
                              return (
                                <React.Fragment key={j}>
                                  <Grid container spacing={4} style={style.gridContainer}>
                                    <Grid item container xs={2} direction="row">
                                      <Grid item xs={6}>
                                        <IconButton
                                          size="large"
                                          color="primary"
                                          onClick={(): void => onUpdateDelivery(address.deliveryAddressCode)}
                                        >
                                          <EditIcon fontSize="inherit" />
                                        </IconButton>
                                      </Grid>
                                      <Grid item xs={6}>
                                        <IconButton
                                          size="large"
                                          color="error"
                                          onClick={(): void =>
                                            onDeliveryDeleteButton({
                                              id: address.deliveryAddressCode,
                                              name: address.name + " " + (address.fullAddress ?? ""),
                                            })
                                          }
                                        >
                                          <DeleteIcon fontSize="inherit" />
                                        </IconButton>
                                      </Grid>
                                    </Grid>
                                    {DeliveryGridKeys.filter(
                                      (gridKey: DeliveryAddressGridKey): boolean => gridKey.displayDisabled !== true,
                                    ).map((gridKey: DeliveryAddressGridKey, k: Key): JSX.Element => {
                                      return (
                                        <Grid
                                          key={k}
                                          item
                                          md={gridKey.md ?? 3}
                                          sm={gridKey.sm ?? 5}
                                          xs={gridKey.xs ?? 5}
                                        >
                                          <Typography key={1} style={style.gridLabel} align={gridKey.align ?? "left"}>
                                            {gridKey.label}
                                          </Typography>
                                          <Typography key={2} align={gridKey.align ?? "left"}>
                                            {(address as Record<string, unknown>)[gridKey.key] as string}
                                          </Typography>
                                        </Grid>
                                      );
                                    })}
                                  </Grid>
                                  <Divider />
                                </React.Fragment>
                              );
                            },
                          )}
                          <Container style={style.buttonContainer}>
                            <Button
                              variant="contained"
                              size="large"
                              color="success"
                              onClick={(): void => onNewDeliveryAddress(partner)}
                            >
                              Szállítási cím hozzáadása
                            </Button>
                            <Button variant="contained" onClick={(): void => onUpdate(partner.partnerCode)}>
                              Ügyfél szerkesztése
                            </Button>
                          </Container>
                        </AccordionDetails>
                      </Accordion>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100, 250]}
        component="div"
        count={maxData}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      {/* Delete modal */}
      <Modal open={isShowingDeleteDeliveryModal} onClose={(): void => setShowingDeleteDeliveryModal(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            border: "2px solid #000",
            boxShadow: "24",
            p: 4,
          }}
        >
          <Typography variant="h6">Biztosan törölni akarod a(z) {toDeleteDelivery.name}-t?</Typography>
          <Container style={style.buttonContainer}>
            <Button variant="contained" onClick={(): void => setShowingDeleteDeliveryModal(false)}>
              Mégse
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={(): void => {
                void deleteDelivery();
              }}
            >
              Törlés
            </Button>
          </Container>
        </Box>
      </Modal>

      {/* Add modal */}
      <Dialog
        TransitionComponent={Transition}
        maxWidth={false}
        open={isSetterOpen}
        onClose={(): void => setSetterOpen(false)}
      >
        <AddPartner id={updateID} setSetterOpen={setSetterOpen} getPartners={getPartners} />
      </Dialog>

      {/* Add address modal */}
      <Dialog
        TransitionComponent={Transition}
        maxWidth={false}
        open={isDeliverySetterOpen}
        onClose={(): void => setDeliverySetterOpen(false)}
      >
        <AddDeliveryAddress
          partnerForDelivery={partnerForDelivery}
          id={updateDeliveryID}
          setDeliverySetterOpen={setDeliverySetterOpen}
          getPartners={getPartners}
        />
      </Dialog>
    </Paper>
  );
};

export default Partners;
